// src/features/dashboard/teacher/AddStudentsSection.jsx
import React, { useState, useEffect } from 'react';
import { SUBJECTS } from '../../../data/subjects';
import { GRADE_LEVELS } from '../../../data/gradeLevels';
import { CLASS_STREAMS } from '../../../data/classStreams';
import { splitGrade } from '../../../utils/gradeUtils';
import ImportStudentsModal from './ImportStudentsModal';

const AddStudentsSection = ({
  mode = 'existing',
  classDetails = {},
  onClassDetailsChange,
  onStudentsAdded,
  onCancel,
  teacherName,
  teacherEmail,
  organisationId,
  school,
  province,
  district,
  circuit,
}) => {
  const [students, setStudents] = useState([
    { name: '', surname: '', email: '', gender: '' }
  ]);
  const [showImportModal, setShowImportModal] = useState(false);

  // Local grade fields for new class mode
  const initialGrade = classDetails.grade || '';
  const { gradeLevel: initLevel, stream: initStream } = splitGrade(initialGrade);
  const [gradeLevel, setGradeLevel] = useState(initLevel || '');
  const [stream, setStream] = useState(initStream || '');

  // ✅ Local term data state
  const [termData, setTermData] = useState(
    classDetails.termData && classDetails.termData.length > 0
      ? classDetails.termData
      : [{ term: '', termStart: '', termEnd: '' }]
  );

  // Update parent's grade when local fields change
  useEffect(() => {
    if (mode === 'newClass' && onClassDetailsChange) {
      const combined = `${gradeLevel}${stream}`.trim();
      onClassDetailsChange('grade', combined);
    }
  }, [gradeLevel, stream, onClassDetailsChange, mode]);

  // ✅ Update parent's termData when local termData changes
  useEffect(() => {
    if (mode === 'newClass' && onClassDetailsChange) {
      const validTerms = termData.filter(t => t.term && t.termStart && t.termEnd);
      onClassDetailsChange('termData', validTerms);
    }
  }, [termData, onClassDetailsChange, mode]);

  const schedule = classDetails.schedule || (mode === 'newClass' ? [{ day: '', startTime: '', duration: '' }] : []);

  // ---- Term handlers ----
  const addTermRow = () => {
    setTermData([...termData, { term: '', termStart: '', termEnd: '' }]);
  };

  const removeTermRow = (index) => {
    if (termData.length <= 1) {
      alert('You need at least one term.');
      return;
    }
    const newTermData = termData.filter((_, i) => i !== index);
    setTermData(newTermData);
  };

  const updateTerm = (index, field, value) => {
    const newTermData = [...termData];
    newTermData[index][field] = value;
    setTermData(newTermData);
  };

  // ---- Student handlers ----
  const addRow = () => {
    setStudents([...students, { name: '', surname: '', email: '', gender: '' }]);
  };

  const removeRow = (index) => {
    const newStudents = students.filter((_, i) => i !== index);
    setStudents(newStudents);
  };

  const handleChange = (index, field, value) => {
    const updated = [...students];
    updated[index][field] = value;
    setStudents(updated);
  };

  const handleClassDetailChange = (field, value) => {
    if (onClassDetailsChange) {
      onClassDetailsChange(field, value);
    }
  };

  // Session management
  const addSession = () => {
    const newSchedule = [...schedule, { day: '', startTime: '', duration: '' }];
    handleClassDetailChange('schedule', newSchedule);
  };

  const removeSession = (index) => {
    if (schedule.length <= 1) {
      alert('You need at least one session.');
      return;
    }
    const newSchedule = schedule.filter((_, i) => i !== index);
    handleClassDetailChange('schedule', newSchedule);
  };

  const updateSession = (index, field, value) => {
    const newSchedule = [...schedule];
    newSchedule[index][field] = value;
    handleClassDetailChange('schedule', newSchedule);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validStudents = students.filter(s => s.name && s.surname && s.email);
    if (validStudents.length === 0) {
      alert('Please add at least one valid student.');
      return;
    }
    if (mode === 'newClass') {
      if (!classDetails.subject || !classDetails.grade) {
        alert('Please fill in Subject and Grade for the new class.');
        return;
      }
      const validSessions = schedule.filter(s => s.day && s.startTime && s.duration);
      if (validSessions.length === 0) {
        alert('Please add at least one valid session (day, start time, duration).');
        return;
      }
    }
    // ✅ Include termData in classDetails
    const validTerms = termData.filter(t => t.term && t.termStart && t.termEnd);
    const classDetailsWithTerms = {
      ...classDetails,
      termData: validTerms,
    };
    await onStudentsAdded(validStudents, classDetailsWithTerms);
    setStudents([{ name: '', surname: '', email: '', gender: '' }]);
  };

  const handleImportComplete = (importedStudents) => {
    const currentValid = students.filter(s => s.name && s.surname && s.email);
    const existingEmails = new Set(currentValid.map(s => s.email.toLowerCase().trim()));
    const newStudents = importedStudents.filter(s => !existingEmails.has(s.email.toLowerCase().trim()));
    const studentsWithGender = newStudents.map(s => ({ ...s, gender: s.gender || '' }));
    const merged = [...currentValid, ...studentsWithGender];
    setStudents(merged.length > 0 ? merged : [{ name: '', surname: '', email: '', gender: '' }]);
    setShowImportModal(false);
  };

  return (
    <div className="bg-gray-50 p-4 rounded border mt-4">
      <h3 className="text-lg font-semibold mb-3">
        {mode === 'newClass' ? 'Create New Class & Add Students' : 'Add Students'}
      </h3>
      <form onSubmit={handleSubmit}>
        {/* Class details – only shown in 'newClass' mode */}
        {mode === 'newClass' && (
          <div className="space-y-4 mb-4 p-3 bg-white rounded border">
            <div className="grid grid-cols-2 gap-3">
              {/* Subject – searchable input */}
              <div>
                <label className="block text-sm font-medium">Subject *</label>
                <input
                  type="text"
                  value={classDetails.subject || ''}
                  onChange={e => handleClassDetailChange('subject', e.target.value)}
                  list="subject-options"
                  className="w-full border rounded px-2 py-1"
                  placeholder="Type or select a subject"
                  required
                />
                <datalist id="subject-options">
                  {SUBJECTS.map(subj => (
                    <option key={subj} value={subj} />
                  ))}
                </datalist>
              </div>

              {/* Grade Level + Stream */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium">Grade Level *</label>
                  <select
                    value={gradeLevel}
                    onChange={e => setGradeLevel(e.target.value)}
                    className="w-full border rounded px-2 py-1"
                    required
                  >
                    <option value="">Select Grade</option>
                    {GRADE_LEVELS.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium">Stream</label>
                  <select
                    value={stream}
                    onChange={e => setStream(e.target.value)}
                    className="w-full border rounded px-2 py-1"
                  >
                    <option value="">None</option>
                    {CLASS_STREAMS.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium">Google Classroom Link</label>
                <input
                  type="text"
                  value={classDetails.googleClassroomLink || ''}
                  onChange={e => handleClassDetailChange('googleClassroomLink', e.target.value)}
                  className="w-full border rounded px-2 py-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Zoom Link</label>
                <input
                  type="text"
                  value={classDetails.zoomLink || ''}
                  onChange={e => handleClassDetailChange('zoomLink', e.target.value)}
                  className="w-full border rounded px-2 py-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Zoom Password</label>
                <input
                  type="text"
                  value={classDetails.zoomPassword || ''}
                  onChange={e => handleClassDetailChange('zoomPassword', e.target.value)}
                  className="w-full border rounded px-2 py-1"
                />
              </div>
            </div>

            {/* ===== TERM DATA SECTION ===== */}
            <div className="border-t pt-3">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium">Term Dates</h4>
                <button
                  type="button"
                  onClick={addTermRow}
                  className="text-sm bg-purple-600 text-white px-2 py-1 rounded hover:bg-purple-700"
                >
                  + Add Term
                </button>
              </div>
              {termData.map((term, idx) => (
                <div key={idx} className="grid grid-cols-3 gap-2 mb-2 items-end">
                  <div>
                    <label className="block text-xs font-medium">Term Name</label>
                    <input
                      type="text"
                      value={term.term || ''}
                      onChange={e => updateTerm(idx, 'term', e.target.value)}
                      placeholder="e.g., Term 1"
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium">Start Date</label>
                    <input
                      type="date"
                      value={term.termStart || ''}
                      onChange={e => updateTerm(idx, 'termStart', e.target.value)}
                      className="w-full border rounded px-2 py-1 text-sm"
                    />
                  </div>
                  <div className="flex items-end gap-1">
                    <div className="flex-1">
                      <label className="block text-xs font-medium">End Date</label>
                      <input
                        type="date"
                        value={term.termEnd || ''}
                        onChange={e => updateTerm(idx, 'termEnd', e.target.value)}
                        className="w-full border rounded px-2 py-1 text-sm"
                      />
                    </div>
                    {termData.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTermRow(idx)}
                        className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                        title="Remove term"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {termData.length === 0 && (
                <p className="text-sm text-gray-500">No terms added. Click "+ Add Term" to add one.</p>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Terms set here will be applied to all students in this class (stored in their schedule).
              </p>
            </div>

            {/* Sessions section */}
            <div className="border-t pt-3">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium">Class Sessions *</h4>
                <button
                  type="button"
                  onClick={addSession}
                  className="text-sm bg-blue-600 text-white px-2 py-1 rounded hover:bg-blue-700"
                >
                  + Add Session
                </button>
              </div>
              {schedule.map((session, idx) => (
                <div key={idx} className="grid grid-cols-3 gap-2 mb-2 items-end">
                  <div>
                    <label className="block text-xs font-medium">Day</label>
                    <input
                      type="text"
                      value={session.day || ''}
                      onChange={e => updateSession(idx, 'day', e.target.value)}
                      placeholder="e.g., Monday"
                      className="w-full border rounded px-2 py-1 text-sm"
                      required={mode === 'newClass'}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium">Start Time</label>
                    <input
                      type="text"
                      value={session.startTime || ''}
                      onChange={e => updateSession(idx, 'startTime', e.target.value)}
                      placeholder="e.g., 09:00"
                      className="w-full border rounded px-2 py-1 text-sm"
                      required={mode === 'newClass'}
                    />
                  </div>
                  <div className="flex items-end gap-1">
                    <div className="flex-1">
                      <label className="block text-xs font-medium">Duration (min)</label>
                      <input
                        type="number"
                        value={session.duration || ''}
                        onChange={e => updateSession(idx, 'duration', e.target.value)}
                        placeholder="60"
                        className="w-full border rounded px-2 py-1 text-sm"
                        required={mode === 'newClass'}
                      />
                    </div>
                    {schedule.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSession(idx)}
                        className="text-red-600 hover:text-red-800 text-sm px-1 py-1"
                        title="Remove session"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {schedule.length === 0 && (
                <p className="text-sm text-gray-500">No sessions added. Click "+ Add Session" to add one.</p>
              )}
            </div>
          </div>
        )}

        {/* Student table with gender column */}
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-200">
                <th className="px-2 py-1 text-left">Name</th>
                <th className="px-2 py-1 text-left">Surname</th>
                <th className="px-2 py-1 text-left">Email</th>
                <th className="px-2 py-1 text-left">Gender</th>
                <th className="px-2 py-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map((student, idx) => (
                <tr key={idx} className="border-t">
                  <td className="px-2 py-1">
                    <input
                      type="text"
                      value={student.name}
                      onChange={e => handleChange(idx, 'name', e.target.value)}
                      className="w-full border rounded px-2 py-1"
                      required
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="text"
                      value={student.surname}
                      onChange={e => handleChange(idx, 'surname', e.target.value)}
                      className="w-full border rounded px-2 py-1"
                      required
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="email"
                      value={student.email}
                      onChange={e => handleChange(idx, 'email', e.target.value)}
                      className="w-full border rounded px-2 py-1"
                      required
                    />
                  </td>
                  <td className="px-2 py-1">
                    <select
                      value={student.gender || ''}
                      onChange={e => handleChange(idx, 'gender', e.target.value)}
                      className="w-full border rounded px-2 py-1"
                    >
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </td>
                  <td className="px-2 py-1 text-center">
                    {students.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        className="text-red-600 hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            onClick={addRow}
            className="bg-gray-300 px-3 py-1 rounded hover:bg-gray-400"
          >
            + Add Row
          </button>
          <button
            type="button"
            onClick={() => {
              if (mode === 'newClass' && (!classDetails.subject || !classDetails.grade)) {
                alert('Please fill in Subject and Grade before importing students.');
                return;
              }
              setShowImportModal(true);
            }}
            disabled={mode === 'newClass' && (!classDetails.subject || !classDetails.grade)}
            className={`bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 ${
              mode === 'newClass' && (!classDetails.subject || !classDetails.grade)
                ? 'opacity-50 cursor-not-allowed'
                : ''
            }`}
            title={
              mode === 'newClass' && (!classDetails.subject || !classDetails.grade)
                ? 'Please fill in Subject and Grade first.'
                : 'Import students from an existing class'
            }
          >
            📥 Import from existing class
          </button>
          <button
            type="submit"
            className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
          >
            {mode === 'newClass' ? 'Create Class & Add Students' : 'Add Students'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-300 px-4 py-1 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Pre-filled metadata info */}
      <div className="mt-3 text-sm text-gray-600">
        <p><strong>Organisation:</strong> {organisationId} – {school}</p>
        <p><strong>Location:</strong> {province}, {district}, {circuit}</p>
        <p><strong>Teacher:</strong> {teacherName}</p>
        {schedule.length > 0 && (
          <div>
            <strong>Sessions:</strong>
            <ul className="list-disc list-inside">
              {schedule.map((s, idx) => (
                <li key={idx}>{s.day} at {s.startTime} ({s.duration} min)</li>
              ))}
            </ul>
          </div>
        )}
        {/* ✅ Show terms in the info section */}
        {termData.filter(t => t.term && t.termStart && t.termEnd).length > 0 && (
          <div>
            <strong>Terms:</strong>
            <ul className="list-disc list-inside">
              {termData.filter(t => t.term && t.termStart && t.termEnd).map((t, idx) => (
                <li key={idx}>
                  {t.term}: {new Date(t.termStart).toLocaleDateString()} – {new Date(t.termEnd).toLocaleDateString()}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Import Modal */}
      <ImportStudentsModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        currentClass={null}
        targetMetadata={{
          subject: classDetails.subject,
          grade: classDetails.grade,
          googleClassroomLink: classDetails.googleClassroomLink || '',
          schedule: schedule,
          termData: termData.filter(t => t.term && t.termStart && t.termEnd), // ✅ Pass termData
          zoomLink: classDetails.zoomLink || '',
          zoomPassword: classDetails.zoomPassword || '',
          teacher: teacherEmail || teacherName || '',
        }}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default AddStudentsSection;